import React, { useState, useEffect, useContext } from 'react';
import { Box, Button, Stack, Typography, Avatar, Paper, Popper, ClickAwayListener, InputAdornment, Divider, CircularProgress } from '@mui/material';
import { Icon } from './icon/icon.component';
import { withStyles } from '@mui/styles';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { getLocations } from 'shared/common.api';
import { UserStateContext } from 'context/user-state-context';
import CONSTANTS from 'shared/constants';
const SearchTextField = withStyles({
  root: {
    '& .MuiOutlinedInput-root': {
      'backgroundColor': '#F1F3F4',
      'borderRadius': `20px`,
      '& fieldset': {
        border: 'none'
      }
    }
  }
})(TextField);

/**
 * LocationPicker Component
 * A dropdown component that mimics the company picker styling.
 *
 * @param {string} value - The selected location ID
 * @param {function} onChange - Callback function when location is selected. Receives the full location object.
 * @param {boolean} userLocations - Optional filter for user_locations (default: false)
 * @param {string} placeholder - Placeholder text (default: "Select a location")
 * @param {Set<string>|null} enabledLocationIds - When provided, only locations in this set are selectable; others appear greyed out. Pass null to enable all.
 */
export const LocationPicker = ({
  value,
  onChange,
  userLocations = false,
  placeholder = 'Select a location',
  enabledLocationIds = null
}) => {
  const {
    asCompany
  } = useContext(UserStateContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [locations, setLocations] = useState([]);
  const [locationsCount, setLocationsCount] = useState(0);
  const [search, setSearch] = useState('');
  const [avatarHover, setAvatarHover] = useState(null);
  const [recentlyUsedLocations, setRecentlyUsedLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [paginationOffset, setPaginationOffset] = useState(0);
  const scrollContainerRef = React.useRef(null);

  // Load recently used locations from localStorage
  useEffect(() => {
    if (!asCompany?.id) return;
    const storageKey = getStorageKey();
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        setRecentlyUsedLocations(JSON.parse(stored));
      } catch (e) {
        console.error('Error parsing recently used locations:', e);
        setRecentlyUsedLocations([]);
      }
    }
  }, [asCompany?.id]);

  // Load locations when search changes or dropdown opens
  useEffect(() => {
    if (!asCompany?.id || !open) return;
    setLocations([]);
    setAvailableLocations([]);
    setHasMore(true);
    setIsLoading(true);
    const recentIds = recentlyUsedLocations.map(loc => loc.id);
    const params = {
      company: asCompany.id,
      search,
      limit: 30,
      ordering: 'friendly_name',
      user_locations: userLocations
    };
    const syncRecentlyUsed = results => {
      const accessibleIds = results.map(loc => loc.id);
      const filtered = recentlyUsedLocations.filter(loc => accessibleIds.includes(loc.id));
      if (filtered.length !== recentlyUsedLocations.length) {
        setRecentlyUsedLocations(filtered);
        localStorage.setItem(getStorageKey(), JSON.stringify(filtered));
      }
    };
    getLocations({
      ...params,
      offset: 0
    }).then(({
      data
    }) => {
      syncRecentlyUsed(data.results);
      const nonRecentLocs = data.results.filter(loc => !recentIds.includes(loc.id));
      if (enabledLocationIds && enabledLocationIds.size > 0) {
        // Partition each page into enabled (selectable) vs others (greyed)
        // client-side via the Set. Previously every enabled id was packed
        // into the URL (id_in / exclude_ids); for companies with many
        // calendar locations that pushed the request URL past gunicorn's
        // request-line limit -> 414, which the browser reported as a
        // misleading CORS / Network error and left the picker empty (CIN-6183).
        setAvailableLocations(nonRecentLocs.filter(loc => enabledLocationIds.has(loc.id)));
        setLocations(nonRecentLocs.filter(loc => !enabledLocationIds.has(loc.id)));
      } else {
        setLocations(nonRecentLocs);
      }
      setLocationsCount(data.count);
      setHasMore(data.results.length === 30);
      setPaginationOffset(data.results.length);
      setIsLoading(false);
    }).catch(err => {
      console.error('Error loading locations:', err);
      setIsLoading(false);
    });
  }, [asCompany?.id, search, open, userLocations, recentlyUsedLocations]);

  // Load more locations
  const loadMoreLocations = () => {
    if (!asCompany?.id || !hasMore || isLoading) return;
    setIsLoading(true);
    getLocations({
      company: asCompany.id,
      search,
      limit: 30,
      offset: paginationOffset,
      ordering: 'friendly_name',
      user_locations: userLocations
    }).then(({
      data
    }) => {
      const recentIds = recentlyUsedLocations.map(loc => loc.id);
      const newLocs = data.results.filter(loc => !recentIds.includes(loc.id));

      // Partition appended pages the same way the initial load does, so
      // enabled locations keep landing in the selectable section as the user
      // scrolls (no ids are sent in the URL -- see CIN-6183).
      if (enabledLocationIds && enabledLocationIds.size > 0) {
        setAvailableLocations(prev => [...prev, ...newLocs.filter(loc => enabledLocationIds.has(loc.id))]);
        setLocations(prev => [...prev, ...newLocs.filter(loc => !enabledLocationIds.has(loc.id))]);
      } else {
        setLocations(prev => [...prev, ...newLocs]);
      }
      setHasMore(data.results.length === 30);
      setPaginationOffset(prev => prev + data.results.length);
      setIsLoading(false);
    });
  };

  // Handle scroll to load more
  const handleScroll = event => {
    const container = event.target;
    const bottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50;
    if (bottom && hasMore && !isLoading) {
      loadMoreLocations();
    }
  };

  // Load the selected location details
  useEffect(() => {
    if (!value || !asCompany?.id) {
      setSelectedLocation(null);
      return;
    }

    // Check if we already have it in locations or recent locations
    const found = [...locations, ...recentlyUsedLocations].find(loc => loc.id === value);
    if (found) {
      setSelectedLocation(found);
    } else {
      // Fetch it
      getLocations({
        company: asCompany.id,
        id: value,
        user_locations: userLocations
      }).then(({
        data
      }) => {
        if (data.results && data.results.length > 0) {
          setSelectedLocation(data.results[0]);
        }
      });
    }
  }, [value, asCompany?.id, locations, recentlyUsedLocations, userLocations]);
  const getStorageKey = () => {
    const env = CONSTANTS.ENV === 'LOCAL' ? 'Local' : CONSTANTS.ENV === 'DEV' ? 'Dev' : '';
    return `recentlyUsedLocations_${asCompany.id}${env}`;
  };
  const handleOpen = event => {
    setAnchorEl(open ? null : event.currentTarget);
  };
  const handleClose = () => {
    setSearch('');
    setAnchorEl(null);
  };
  const handleLocationChange = location => {
    setSelectedLocation(location);
    handleClose();
    handleRecentlyUsedLocations(location);
    if (onChange) {
      onChange(location);
    }
  };
  const handleRecentlyUsedLocations = location => {
    let recents = [...recentlyUsedLocations];

    // Remove if already exists
    recents = recents.filter(loc => loc.id !== location.id);

    // Add to beginning
    recents.unshift(location);

    // Keep only top 3
    if (recents.length > 3) {
      recents = recents.slice(0, 3);
    }
    const storageKey = getStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(recents));
    setRecentlyUsedLocations(recents);
  };
  const resetSearch = () => {
    setSearch('');
  };
  const getInitials = name => {
    if (!name) return '';
    return name.split(' ').filter((segment, i) => i < 2).map(segment => segment[0]).join('').toUpperCase();
  };
  return <>
      <Box sx={{
      cursor: 'pointer',
      borderRadius: '8px',
      alignItems: 'center',
      display: 'flex',
      height: '40px',
      minWidth: '266px',
      backgroundColor: open ? '#DAEFFF' : '#FFFFFF'
    }} onClick={handleOpen}>
        <Stack sx={{
        display: 'flex',
        flexGrow: 1
      }} direction="row">
          <Box sx={{
          px: '12px'
        }}>
            <Avatar sx={{
            height: '30px',
            width: '30px'
          }}>{getInitials(selectedLocation?.friendly_name)}</Avatar>
          </Box>
          <Box sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '180px',
          alignItems: 'center',
          alignContent: 'center',
          display: 'flex'
        }}>
            <Typography sx={{
            color: '#616161F2'
          }} noWrap>
              {selectedLocation?.friendly_name || placeholder}
            </Typography>
          </Box>
          <Box sx={{
          flexGrow: 1
        }} />
          {open ? <Box sx={{
          pl: '18px',
          position: 'relative',
          transform: 'rotate(180deg)',
          bottom: '5px'
        }}>
              <Icon name="custom-keyboard-arrow-down" />
            </Box> : <Box sx={{
          pr: '18px',
          position: 'relative',
          top: '5px'
        }}>
              <Icon name="custom-keyboard-arrow-down" />
            </Box>}
        </Stack>
      </Box>

      <Popper anchorEl={anchorEl} sx={{
      zIndex: 1400
    }} open={open} placement="bottom">
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={0} onScroll={handleScroll} sx={{
          'borderRadius': '16px',
          'overflow': 'visible',
          'width': '266px',
          'overflowY': 'scroll',
          'maxHeight': '66vh',
          'scrollbarWidth': 'thin',
          '&::-webkit-scrollbar': {
            width: '0.4em'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            marginTop: '20px',
            marginBottom: '20px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555'
          },
          'filter': 'drop-shadow(0px 0px 4px rgba(0,0,0,0.25))',
          'mt': 1.5,
          '& .MuiAvatar-root': {
            width: 30,
            height: 30
          },
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 1400
          }
        }}>
            <Box sx={{
            pb: 1
          }}>
              <Box sx={{
              pt: 1,
              position: 'sticky',
              top: '0px',
              backgroundColor: '#fff',
              zIndex: 1405
            }}>
                <Box sx={{
                px: 1,
                pb: 1
              }}>
                  <SearchTextField placeholder="Search locations" fullWidth autoFocus sx={{
                  height: '40px'
                }} value={search} onChange={e => setSearch(e.target.value)} InputProps={{
                  startAdornment: <InputAdornment position="start">
                          <Icon name="custom-search" />
                        </InputAdornment>,
                  endAdornment: !!search && <InputAdornment onClick={resetSearch} position="start" sx={{
                    cursor: 'pointer'
                  }}>
                          <CloseIcon />
                        </InputAdornment>
                }} />
                </Box>

                {!search && recentlyUsedLocations.length > 0 && <>
                    <Box sx={{
                  py: 1,
                  pl: 1
                }}>
                      <Typography variant="body2">Recently Used Locations</Typography>
                    </Box>
                    <Stack>
                      {recentlyUsedLocations.map(location => {
                    const isNotEnabled = enabledLocationIds && !enabledLocationIds.has(location.id);
                    return <Button disabled={value === location.id || isNotEnabled} onMouseEnter={() => !isNotEnabled && setAvatarHover(location.id)} onMouseLeave={() => !isNotEnabled && setAvatarHover(null)} onClick={() => !isNotEnabled && handleLocationChange(location)} variant="text" key={location.id} fullWidth sx={{
                      'cursor': isNotEnabled ? 'default' : 'pointer',
                      'textTransform': 'none',
                      'justifyContent': 'flex-start',
                      'borderRadius': '0px',
                      'color': isNotEnabled ? '#B0B0B0' : '#1D252D',
                      'borderLeft': '2px solid #fff',
                      'p': 0,
                      '&:hover': isNotEnabled ? {} : {
                        backgroundColor: '#fff',
                        color: '#53A6D6',
                        borderLeft: '2px solid #53A6D6'
                      },
                      '&.Mui-disabled': isNotEnabled ? {
                        opacity: 1
                      } : {}
                    }}>
                            <Stack sx={{
                        p: 1,
                        color: 'inherit',
                        alignItems: 'center'
                      }} direction="row">
                              <Avatar sx={{
                          backgroundColor: isNotEnabled ? '#E0E0E0' : avatarHover === location.id ? '#53A6D6' : '#BEBEBE'
                        }}>
                                {getInitials(location.friendly_name)}
                              </Avatar>
                              <Typography sx={{
                          fontSize: 14,
                          top: '5px',
                          position: 'relative',
                          pl: '10px',
                          color: isNotEnabled ? '#B0B0B0' : 'inherit',
                          textAlign: 'left',
                          maxWidth: '200px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          wordBreak: 'break-word'
                        }}>
                                {location.friendly_name}
                              </Typography>
                            </Stack>
                          </Button>;
                  })}
                    </Stack>
                    <Divider flexItem sx={{
                  pt: 2
                }} />
                  </>}
              </Box>

              <Box display="flex" flexDirection="column">
                {availableLocations.map(location => <Button disabled={value === location.id} onMouseEnter={() => setAvatarHover(location.id)} onMouseLeave={() => setAvatarHover(null)} onClick={() => handleLocationChange(location)} variant="text" key={location.id} fullWidth sx={{
                'textTransform': 'none',
                'justifyContent': 'flex-start',
                'borderRadius': '0px',
                'color': '#1D252D',
                'borderLeft': '2px solid #fff',
                'p': 0,
                '&:hover': {
                  backgroundColor: '#fff',
                  color: '#53A6D6',
                  borderLeft: '2px solid #53A6D6'
                }
              }}>
                    <Stack sx={{
                  p: 1,
                  color: 'inherit',
                  alignItems: 'center'
                }} direction="row">
                      <Avatar sx={{
                    backgroundColor: avatarHover === location.id ? '#53A6D6' : '#BEBEBE'
                  }}>
                        {getInitials(location.friendly_name)}
                      </Avatar>
                      <Typography sx={{
                    fontSize: 14,
                    top: '5px',
                    position: 'relative',
                    pl: '10px',
                    textAlign: 'left',
                    maxWidth: '200px',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word'
                  }}>
                        {location.friendly_name}
                      </Typography>
                    </Stack>
                  </Button>)}
                {availableLocations.length > 0 && locations.length > 0 && <Divider flexItem sx={{
                my: 1
              }} />}
                {locations.map(location => {
                const isNotEnabled = !!enabledLocationIds;
                return <Button disabled={value === location.id || isNotEnabled} onMouseEnter={() => !isNotEnabled && setAvatarHover(location.id)} onMouseLeave={() => !isNotEnabled && setAvatarHover(null)} onClick={() => !isNotEnabled && handleLocationChange(location)} variant="text" key={location.id} fullWidth sx={{
                  'cursor': isNotEnabled ? 'default' : 'pointer',
                  'textTransform': 'none',
                  'justifyContent': 'flex-start',
                  'borderRadius': '0px',
                  'color': isNotEnabled ? '#B0B0B0' : '#1D252D',
                  'borderLeft': '2px solid #fff',
                  'p': 0,
                  '&:hover': isNotEnabled ? {} : {
                    backgroundColor: '#fff',
                    color: '#53A6D6',
                    borderLeft: '2px solid #53A6D6'
                  },
                  '&.Mui-disabled': isNotEnabled ? {
                    opacity: 1
                  } : {}
                }}>
                      <Stack sx={{
                    p: 1,
                    color: 'inherit',
                    alignItems: 'center'
                  }} direction="row">
                        <Avatar sx={{
                      backgroundColor: isNotEnabled ? '#E0E0E0' : avatarHover === location.id ? '#53A6D6' : '#BEBEBE'
                    }}>
                          {getInitials(location.friendly_name)}
                        </Avatar>
                        <Typography sx={{
                      fontSize: 14,
                      top: '5px',
                      position: 'relative',
                      pl: '10px',
                      color: isNotEnabled ? '#B0B0B0' : 'inherit',
                      textAlign: 'left',
                      maxWidth: '200px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word'
                    }}>
                          {location.friendly_name}
                        </Typography>
                      </Stack>
                    </Button>;
              })}
                {!isLoading && availableLocations.length === 0 && locations.length === 0 && <Box sx={{
                p: 2
              }}>No results</Box>}
                {isLoading && <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 2
              }}>
                    <CircularProgress size={24} />
                  </Box>}
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>;
};